/**
 * TreeMapSwimlane Molecule
 *
 * Represents a horizontal section (swimlane) in a column, containing spheres for a specific parent.
 * Each swimlane independently loads its own children using the useSwimlaneData hook.
 */

import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { TreeMapSwimlaneProps } from '../../organisms/TreeMap/types';
import { resolveLabel } from '../../../utils/resolveLabel';
import { useSwimlaneData } from '../../organisms/TreeMap/useSwimlaneData';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TreeMapCard from '../TreeMapCard/TreeMapCard';
import './TreeMapSwimlane.scss';

const TreeMapSwimlane: React.FC<TreeMapSwimlaneProps> = ({
  parentSphere,
  level,
  viewConfig,
  baseConfig,
  editable,
  cardView,
  organizationId,
  schemas,
  apiClient,
  defaultPageSize = 5,
  onSphereDrop,
  onAddClick,
  onAIClick,
  onSphereSelect,
  contextSystemName,
  rootSphereId,
}) => {
  const [selectedSphereId, setSelectedSphereId] = useState<string | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);  // Start expanded
  const { t, i18n } = useTranslation();

  const levelLabel = resolveLabel(level.label, i18n.language);

  // Load this swimlane's data
  const {
    spheres,
    loading,
    error,
    hasMore,
    totalCount,
    loadMore
  } = useSwimlaneData({
    parentSphereId: parentSphere.id,
    levelConfig: level,
    apiClient,
    enabled: isExpanded,  // Only load when expanded
    defaultPageSize,
    contextSystemName,
    rootSphereId
  });

  // Handle drag over for drop zone
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.sphereId) {
        onSphereDrop(data.sphereId);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  // Handle sphere selection
  const handleSphereSelect = (sphereId: string) => {
    setSelectedSphereId(sphereId);
    onSphereSelect?.(sphereId);
  };

  return (
    <div className={`tree-map-swimlane ${isExpanded ? 'tree-map-swimlane--expanded' : ''} ${error ? 'tree-map-swimlane--error' : ''}`}>
      {/* Swimlane Header */}
      <div className="tree-map-swimlane__header">
        <div className="tree-map-swimlane__title">
          <button
            className="tree-map-swimlane__toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? t('common.collapse') : t('common.expand')}
          >
            <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
          </button>
          <span className="tree-map-swimlane__parent-name">
            {parentSphere.properties.name || t('common.root')}
          </span>
          <span className="tree-map-swimlane__count">
            {loading && totalCount === 0 ? '(...)' : `(${totalCount})`}
          </span>
        </div>

        {editable && isExpanded && !error && (
          <div className="tree-map-swimlane__actions">
            <ButtonAtom
              icon="pi-sparkles"
              variant="secondary"
              size="small"
              onClick={onAIClick}
              tooltip={t('treeMap.swimlane.aiSuggestions')}
            />
            <ButtonAtom
              icon="pi-plus"
              variant="secondary"
              size="small"
              onClick={onAddClick}
              tooltip={t('treeMap.swimlane.addLevel', { levelLabel })}
            />
          </div>
        )}
      </div>

      {/* Swimlane Content - Only show when expanded */}
      {isExpanded && (
        <>
          <section
            className="tree-map-swimlane__content"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            aria-label="Swimlane content"
          >
            {/* Error state */}
            {error && (
              <div className="tree-map-swimlane__error">
                <i className="pi pi-exclamation-triangle" />
                <p>{error}</p>
              </div>
            )}

            {/* Empty state */}
            {!error && spheres.length === 0 && !loading && (
              <div className="tree-map-swimlane__empty">
                <i className="pi pi-inbox" />
                <p>{t('treeMap.swimlane.noItemsYet', { levelLabel })}</p>
                {editable && (
                  <ButtonAtom
                    label={t('treeMap.swimlane.addLevel', { levelLabel })}
                    icon="pi-plus"
                    variant="secondary"
                    size="small"
                    onClick={onAddClick}
                  />
                )}
              </div>
            )}

            {/* Cards */}
            {!error && spheres.map((sphere) => (
              <TreeMapCard
                key={sphere.id}
                sphere={sphere}
                level={level}
                viewConfig={viewConfig}
                baseConfig={baseConfig}
                cardView={cardView}
                editable={editable}
                isSelected={selectedSphereId === sphere.id}
                isDragging={draggingCardId === sphere.id}
                onSelect={() => handleSphereSelect(sphere.id)}
                onExpand={() => {
                  // Handle expand - will be passed from parent
                }}
                onEdit={() => {
                  // Handle edit - will be passed from parent
                }}
                onDelete={() => {
                  // Handle delete - will be passed from parent
                }}
                onDragStart={() => setDraggingCardId(sphere.id)}
                onDragEnd={() => setDraggingCardId(null)}
                schemas={schemas}
              />
            ))}

            {/* Loading state */}
            {loading && (
              <div className="tree-map-swimlane__loading">
                <i className="pi pi-spin pi-spinner" />
                <p>{t('treeMap.swimlane.loadingLevel', { levelLabel })}</p>
              </div>
            )}
          </section>

          {/* Pagination controls - outside grid */}
          {!error && hasMore && !loading && (
            <div className="tree-map-swimlane__pagination">
              <button
                type="button"
                className="tree-map-swimlane__show-more-link"
                onClick={loadMore}
                style={{
                  cursor: 'pointer',
                  color: 'var(--primary-color)',
                  textDecoration: 'underline',
                  fontSize: '0.9rem',
                  padding: '4px 0',
                  background: 'none',
                  border: 'none'
                }}
              >
                {t('common.showMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TreeMapSwimlane;
